import {
  AdsClick,
  History,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  DynamicFeed,
  Factory,
  Lan,
  LocalOffer,
  Person,
  PhoneIphone,
  ReceiptLong,
  Rule,
  ShoppingCart,
  TravelExplore,
  Wifi,
  Campaign,
  Gavel,
  Error,
} from '@mui/icons-material';
import { MultiLevelMenu } from '@react-admin/ra-navigation';
import { ReactElement, useMemo } from 'react';
import { useCanAccess } from 'react-admin';
import CollapsibleResourceMenuItem, { type SubMenuLink } from './CollapsibleResourceMenuItem';
import TrackedErrorsMenuLabel from './TrackedErrorsMenuLabel';

export default function Menu(): ReactElement {
  const clicksByMerchantAccess = useCanAccess({
    resource: 'clicks_by_merchants',
    action: 'list',
  });
  const clicksByReferrerAccess = useCanAccess({
    resource: 'clicks_by_referrers',
    action: 'list',
  });
  const clicksSummaryAccess = useCanAccess({
    resource: 'clicks_summaries',
    action: 'list',
  });
  const merchantsOfferSummaryAccess = useCanAccess({
    resource: 'merchants_offer_summaries',
    action: 'list',
  });
  const actionLogsByUserAccess = useCanAccess({
    resource: 'action_logs_by_users',
    action: 'list',
  });

  const merchantItems = useMemo<SubMenuLink[]>(
    () => [
      {
        key: 'merchants.offer_summary',
        label: 'Offer Summary',
        to: '/reports/merchants/offer-summary',
        visible: merchantsOfferSummaryAccess.canAccess ?? false,
      },
    ],
    [merchantsOfferSummaryAccess.canAccess],
  );
  const actionLogItems = useMemo<SubMenuLink[]>(
    () => [
      {
        key: 'action_logs.by_user',
        label: 'By User',
        to: '/reports/action-logs/by-user',
        visible: actionLogsByUserAccess.canAccess ?? false,
      },
    ],
    [actionLogsByUserAccess.canAccess],
  );
  const clickItems = useMemo<SubMenuLink[]>(
    () => [
      {
        key: 'clicks.by_merchant',
        label: 'By Merchant',
        to: '/reports/clicks/by-merchant',
        visible: clicksByMerchantAccess.canAccess ?? false,
      },
      {
        key: 'clicks.by_referrer',
        label: 'By Referrer',
        to: '/reports/clicks/by-referrer',
        visible: clicksByReferrerAccess.canAccess ?? false,
      },
      {
        key: 'clicks.summary',
        label: 'Summary',
        to: '/reports/clicks/summary',
        visible: clicksSummaryAccess.canAccess ?? false,
      },
    ],
    [
      clicksByMerchantAccess.canAccess,
      clicksByReferrerAccess.canAccess,
      clicksSummaryAccess.canAccess,
    ],
  );

  return (
    <MultiLevelMenu initialOpen>
      <MultiLevelMenu.DashboardItem icon={<DashboardIcon />} />
      <CollapsibleResourceMenuItem
        icon={<History />}
        items={actionLogItems}
        resource="action_logs"
      />
      <MultiLevelMenu.ResourceItem resource="categories" icon={<CategoryIcon />} />
      <MultiLevelMenu.ResourceItem resource="products" icon={<PhoneIphone />} />
      <MultiLevelMenu.ResourceItem resource="offers" icon={<LocalOffer />} />
      <CollapsibleResourceMenuItem
        icon={<ShoppingCart />}
        items={merchantItems}
        resource="merchants"
      />
      <MultiLevelMenu.ResourceItem resource="feeds" icon={<DynamicFeed />} />
      <MultiLevelMenu.ResourceItem resource="registrations" icon={<Person />} />
      <MultiLevelMenu.ResourceItem resource="manufacturers" icon={<Factory />} />
      <MultiLevelMenu.ResourceItem resource="regions" icon={<TravelExplore />} />
      <MultiLevelMenu.ResourceItem resource="affiliate_networks" icon={<Lan />} />
      <MultiLevelMenu.ResourceItem resource="mapping_rules" icon={<Rule />} />
      <CollapsibleResourceMenuItem icon={<AdsClick />} items={clickItems} resource="clicks" />
      <MultiLevelMenu.ResourceItem resource="billing_items" icon={<ReceiptLong />} />
      <MultiLevelMenu.ResourceItem resource="scans" icon={<Wifi />} />
      <MultiLevelMenu.ResourceItem resource="subscriptions" icon={<Campaign />} />
      <MultiLevelMenu.ResourceItem resource="bids" icon={<Gavel />} />
      <MultiLevelMenu.ResourceItem
        resource="tracked_error_issues"
        icon={<Error />}
        label={<TrackedErrorsMenuLabel />}
      />
    </MultiLevelMenu>
  );
}
